/* eslint-disable prettier/prettier */

import { useCallback, useState } from 'react';

import {
	coachingHoursService,
	CoachingHoursFilters,
	CoachingHoursListResponse,
	CoachingHoursLog,
	CreateCoachingHoursPayload,
	UpdateCoachingHoursPayload,
	ReviewCoachingHoursPayload,
	UploadFileResponse,
} from '../services/coachingHours.service';

//
// -------------------------------------------
// CUSTOM HOOK
// -------------------------------------------
//

export const useCoachingHours = () => {
	//
	// -------------------------------------------
	// STATES
	// -------------------------------------------
	//

	const [loading, setLoading] = useState<boolean>(false);

	const [error, setError] = useState<string | null>(null);

	const [logsData, setLogsData] = useState<CoachingHoursListResponse | null>(
		null
	);

	const [selectedLog, setSelectedLog] = useState<CoachingHoursLog | null>(
		null
	);

	//
	// -------------------------------------------
	// CLEAR ERROR
	// -------------------------------------------
	//

	const clearError = () => {
		setError(null);
	};

	//
	// -------------------------------------------
	// UPLOAD FILE
	// -------------------------------------------
	//

	const uploadFile = useCallback(
		async (
			file: File,
			onUploadProgress?: (progress: number) => void
		): Promise<UploadFileResponse | null> => {
			try {
				setLoading(true);
				setError(null);

				const response = await coachingHoursService.uploadFile(
					file,
					onUploadProgress
				);

				return response.data || null;
			} catch (err: any) {
				setError(
					err?.response?.data?.details?.message ||
					err?.response?.data?.message ||
					'Failed to upload file'
				);


				throw err;
			} finally {
				setLoading(false);
			}
		},
		[]
	);

	//
	// -------------------------------------------
	// CREATE LOG
	// -------------------------------------------
	//

	const createLog = useCallback(
		async (
			payload: CreateCoachingHoursPayload
		): Promise<CoachingHoursLog | null> => {
			try {
				setLoading(true);
				setError(null);

				const response = await coachingHoursService.createLog(payload);

				return response.data || null;
			} catch (err: any) {
				setError(
					err?.response?.data?.message ||
					'Failed to create coaching hours log'
				);

				return null;
			} finally {
				setLoading(false);
			}
		},
		[]
	);

	//
	// -------------------------------------------
	// FETCH LOGS
	// -------------------------------------------
	//

	const fetchLogs = useCallback(async (filters?: CoachingHoursFilters) => {
		try {
			setLoading(true);
			setError(null);

			const response = await coachingHoursService.getLogs(filters);

			if (response.data) {
				setLogsData(response.data);
			}

			return response.data;
		} catch (err: any) {
			setError(
				err?.response?.data?.message ||
				'Failed to fetch coaching hours logs'
			);

			return null;
		} finally {
			setLoading(false);
		}
	}, []);

	//
	// -------------------------------------------
	// FETCH SINGLE LOG
	// -------------------------------------------
	//

	const fetchLogById = useCallback(async (logId: string) => {
		try {
			setLoading(true);
			setError(null);

			const response = await coachingHoursService.getLogById(logId);

			if (response.data) {
				setSelectedLog(response.data);
			}

			return response.data;
		} catch (err: any) {
			setError(
				err?.response?.data?.message ||
				'Failed to fetch coaching hours log'
			);

			return null;
		} finally {
			setLoading(false);
		}
	}, []);

	//
	// -------------------------------------------
	// UPDATE LOG
	// -------------------------------------------
	//

	const updateLog = useCallback(
		async (logId: string, payload: UpdateCoachingHoursPayload) => {
			try {
				setLoading(true);
				setError(null);

				const response = await coachingHoursService.updateLog(
					logId,
					payload
				);

				//
				// Update selected log instantly
				//
				if (response.data) {
					setSelectedLog(response.data);
				}

				return response.data;
			} catch (err: any) {
				setError(
					err?.response?.data?.message ||
					'Failed to update coaching hours log'
				);

				return null;
			} finally {
				setLoading(false);
			}
		},
		[]
	);

	//
	// -------------------------------------------
	// REVIEW LOG
	// -------------------------------------------
	//

	const reviewLog = useCallback(
		async (logId: string, payload: ReviewCoachingHoursPayload) => {
			try {
				setLoading(true);
				setError(null);

				const response = await coachingHoursService.reviewLog(
					logId,
					payload
				);

				//
				// Update selected log instantly
				//
				if (response.data) {
					setSelectedLog(response.data);
				}

				return response.data;
			} catch (err: any) {
				setError(
					err?.response?.data?.message ||
					'Failed to review coaching hours log'
				);

				return null;
			} finally {
				setLoading(false);
			}
		},
		[]
	);

	//
	// -------------------------------------------
	// DELETE LOG
	// -------------------------------------------
	//

	const deleteLog = useCallback(async (logId: string) => {
		try {
			setLoading(true);
			setError(null);

			const response = await coachingHoursService.deleteLog(logId);

			return response.success;
		} catch (err: any) {
			setError(
				err?.response?.data?.message ||
				'Failed to delete coaching hours log'
			);

			return false;
		} finally {
			setLoading(false);
		}
	}, []);

	//
	// -------------------------------------------
	// RETURN
	// -------------------------------------------
	//

	return {
		//
		// STATES
		//
		loading,
		error,

		logsData,
		selectedLog,

		//
		// HELPERS
		//
		clearError,

		//
		// API METHODS
		//
		uploadFile,

		createLog,

		fetchLogs,
		fetchLogById,

		updateLog,

		reviewLog,

		deleteLog,
	};
};
